/**
 * src/shell/save/store.ts — localStorage 版 SaveStorePort（引擎 §8.1 端口实现）。
 *
 * - readAll / writeBatch：整键读写，单键 setItem 天然原子；多键批次先序列化再逐键落，
 *   任一失败回滚已写键（localStorage 无事务，靠写前快照回滚）。
 * - 滚动备份：每次 writeBatch 前把"被改键的旧值"压入备份环（最近 3 份），
 *   供存档损坏时一键回滚（引擎 §8.1「备份」承诺的外壳侧）。
 * - 订阅：writeBatch 成功后广播；另监听 window storage 事件跨标签页同步。
 *   React 侧用 useSyncExternalStore 包（见 gm-client/hooks.ts）。
 * - 安全红线：本模块绝不读 apikey 键以外的敏感键；apikey 由 apikey.ts 独立管理。
 */

import type { SaveBundle, SaveStorePort } from '../../gm'
import { BACKUP_RING, BUNDLE_KEYS, backupKey, bundleKey, type BundleKey } from './keys'

function readKey<T>(k: string): T | null {
  try {
    const raw = localStorage.getItem(k)
    return raw ? (JSON.parse(raw) as T) : null
  } catch (err) {
    console.warn('[shell/save] 存档键解析失败：', k, err)
    return null
  }
}

function writeKey(k: string, v: unknown): void {
  localStorage.setItem(k, JSON.stringify(v))
}

// ── 订阅广播 ────────────────────────────────────────────────────────────────

type Listener = () => void
const listeners = new Set<Listener>()

export function subscribeSave(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function notify(): void {
  for (const l of listeners) l()
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key && (e.key.startsWith('thirdplay:v2:') || e.key.startsWith('tp3:'))) notify()
  })
}

// ── 滚动备份（写前快照，环长 3）─────────────────────────────────────────────

interface BackupEntry {
  ts: string
  /** 被覆盖键的旧值（回滚 = 写回这些键） */
  old: Partial<SaveBundle>
}

function pushBackup(old: Partial<SaveBundle>): void {
  try {
    for (let n = BACKUP_RING; n >= 1; n--) {
      const prev = n > 1 ? readKey<BackupEntry>(backupKey(n - 1)) : null
      if (prev) writeKey(backupKey(n), prev)
    }
    writeKey(backupKey(1), { ts: new Date().toISOString(), old } satisfies BackupEntry)
  } catch (err) {
    console.warn('[shell/save] 备份写入失败（不阻断写批次）：', err)
  }
}

/** 一键回滚：把最近一份备份的旧值写回（成功返回 true） */
export function restoreLatestBackup(): boolean {
  const latest = readKey<BackupEntry>(backupKey(1))
  if (!latest) return false
  for (const [k, v] of Object.entries(latest.old)) {
    writeKey(bundleKey(k as BundleKey), v)
  }
  notify()
  return true
}

// ── 端口实现 ────────────────────────────────────────────────────────────────

export class LocalSaveStore implements SaveStorePort {
  async readAll(): Promise<SaveBundle> {
    const out = {} as Record<BundleKey, unknown>
    for (const k of BUNDLE_KEYS) {
      out[k] = readKey(bundleKey(k))
    }
    return out as unknown as SaveBundle
  }

  /**
   * 批量写：先序列化全部值 → 记录旧值备份 → 逐键 setItem；
   * 任一失败用备份回滚已写键后重抛（配额超限等由引擎映射为 STORAGE_FULL）。
   */
  async writeBatch(patch: Partial<SaveBundle>): Promise<void> {
    const keys = Object.keys(patch) as BundleKey[]
    if (keys.length === 0) return
    const old: Partial<SaveBundle> = {}
    const serialized: Array<[string, string]> = []
    for (const k of keys) {
      old[k] = readKey(bundleKey(k)) as never
      serialized.push([bundleKey(k), JSON.stringify(patch[k])])
    }
    pushBackup(old)
    const written: BundleKey[] = []
    try {
      for (let i = 0; i < keys.length; i++) {
        localStorage.setItem(serialized[i][0], serialized[i][1])
        written.push(keys[i])
      }
    } catch (err) {
      // 回滚已写键
      for (const k of written) {
        try {
          const v = old[k]
          if (v === null || v === undefined) localStorage.removeItem(bundleKey(k))
          else localStorage.setItem(bundleKey(k), JSON.stringify(v))
        } catch {
          // 回滚尽力而为
        }
      }
      throw err
    }
    notify()
  }
}

/** 单例（引擎不持有单例——单例策略归外壳，引擎 §8） */
export const localSaveStore = new LocalSaveStore()
